import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ThemeMode } from "../theme";

export type ThemeModePreference = ThemeMode | "system";

interface ThemeModeContextType {
  mode: ThemeModePreference;
  resolvedMode: ThemeMode;
  setMode: (next: ThemeModePreference) => void;
}

export const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

const THEME_MODE_STORAGE_KEY = "theme_mode";

const isThemeModePreference = (value: string | null): value is ThemeModePreference =>
  value === "light" || value === "dark" || value === "system";

const readPersistedMode = (): ThemeModePreference => {
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return isThemeModePreference(stored) ? stored : "light";
};

const getSystemMode = (): ThemeMode =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

interface ThemeModeProviderProps {
  children: ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeModePreference>(readPersistedMode);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  useEffect(() => {
    if (mode !== "system") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? "dark" : "light");
    };
    setSystemMode(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [mode]);

  const setMode = useCallback((next: ThemeModePreference) => {
    setModeState(next);
    localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
  }, []);

  const resolvedMode: ThemeMode = mode === "system" ? systemMode : mode;

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeModeContext = (): ThemeModeContextType => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error("useThemeModeContext must be used within a ThemeModeProvider");
  }
  return ctx;
};
